<script setup lang="ts">
import type { Agent } from '@/core/entities/agent/Agent';
import type { ReelItem } from '~/components/PropertyReelsFeed';

import {
	breakpointsTailwind,
	useBreakpoints,
	useTimeoutFn
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useShortsVideoJsonLd } from '~/composables/jsonLd/useShortsVideoJsonLd';
import {
	consumePropertyIntentPrefetch,
	getPropertyDetailsPath,
	isShortsEntrySource,
	SHORTS_ENTRY_SOURCE,
	useShortsBackgroundBlur,
	useShortsContactModalVideoPause,
	useShortsEntryAvailability,
	useShortsFeedOnboarding,
	useShortsFeedOrchestrator,
	useShortsFeedSeo,
	useShortsNavigation,
	useShortsRouteSync,
	useShortsSpeedState
} from '~/composables/shorts';
import { useShortsEntryContext } from '~/composables/shorts/useShortsEntryContext';
import { useFavoriteStarPulse } from '~/composables/useFavoriteStarPulse';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { loadShaka } from '~/composables/useHlsPlayer/shakaLoader';
import {
	applyShortsEntryRecommendationRule,
	hasShortsAppliedFilters,
	hasShortsListingQueryContext,
	shortsListingQueryToFilters,
	withFeedBaseFilters,
	withImagesEvery
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { useShortsReelLocationSearch } from '~/composables/useShortsFilters/useShortsReelLocationSearch';
import { SHORTS_DEFER_LOGIN_FAVORITE_COUNT } from '~/composables/useToggleFavoriteSync';
import { useShortsHlsWarmup } from '~/composables/useVideoWarmup';
import { MixpanelProps } from '~/core/service/mixpanel';
import { hasShortsVideo } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

import ShortsSearchSuggestDialog from '~/components/Filters/ShortsFiltersBar/ShortsSearchSuggestDialog.vue';
import ShortsSearchSuggestInline from '~/components/Filters/ShortsFiltersBar/ShortsSearchSuggestInline.vue';
import { GlassButton } from '~/components/GlassButton';
import {
	PropertyReelsFeed,
	ReelActionButtons,
	ReelPropertyInfo
} from '~/components/PropertyReelsFeed';
import {
	buildShortsCtaViewMorePayload,
	buildShortsFavoriteEventData,
	buildShortsFormPropertyClickedPayload,
	buildShortsOnboardingCtaPayload
} from '~/components/PropertyReelsFeed/shortsMixpanelPayloads';
import ShortsUnavailableCard from '~/components/PropertyReelsFeed/ShortsUnavailableCard.vue';
import {
	consumeShortsAutoplayBlessing,
	isIOSNativePlayback,
	setIOSMsePreference
} from '~/components/PropertyReelsFeed/useIOSSharedVideo';
import { useShortsPropertyCardTexts } from '~/components/PropertyReelsFeed/useShortsPropertyCardTexts';
import { ShortsOnboarding } from '~/components/ShortsOnboarding';
import { useExploreFilters } from '~/components/VideoExplore/useExploreFilters';

import IconMaterialSymbolsLightArrowBack from '~icons/material-symbols-light/arrow-back';
import IconMaterialSymbolsLightSearch from '~icons/material-symbols-light/search';
import IconMaterialSymbolsLightVolumeOff from '~icons/material-symbols-light/volume-off';
import IconMaterialSymbolsLightVolumeUp from '~icons/material-symbols-light/volume-up';
import LinearPilarLogoRaw from '~icons/pilar/linear-logo?raw&width=1em&height=1em';

definePageMeta({
	layout: false
});

const route = useRoute('shorts-commercialId');
const router = useRouter();
const isClusterFeedEntry = isShortsEntrySource(
	route.query,
	SHORTS_ENTRY_SOURCE.CLUSTER
);
const { navigateToPropertyDetails } = useShortsNavigation();
const mixpanel = useMixpanel();
const userSession = useUserSession();
const getCookieData = useCookieData();

const commercialIdParam = route.params.commercialId;
const commercialId: string = Array.isArray(commercialIdParam)
	? (commercialIdParam[0] ?? '')
	: (commercialIdParam ?? '');

if (!commercialId) {
	throw createError({
		statusCode: 404,
		statusMessage: 'Página não encontrada'
	});
}

// --- SSR: fetch the initial property ---
const { data: initialProperty } = await useAsyncData(
	`shorts:${commercialId}`,
	async () => {
		// A hover/click on a shorts card may have started this exact request —
		// reuse the in-flight promise instead of re-fetching (client no-op miss,
		// always null on SSR).
		const [property, error] = await (consumePropertyIntentPrefetch(
			commercialId
		) ??
			nuxtPropertiesRepository.getOne(
				commercialId,
				userSession.user.value?.id ?? undefined,
				getCookieData()
			));
		if (error || !property) return null;
		return property;
	},
	{ default: () => null }
);

if (!initialProperty.value) {
	throw createError({
		statusCode: 404,
		message: 'Imóvel não encontrado'
	});
}

// SSR head hints: preconnect + preload of the entry video's full HLS chain
// (master → variant playlist → first segment), so the video downloads in
// parallel with the JS bundle instead of after hydration. Degrades to no
// hints on any failure — the player's own fetch flow is untouched.
await useShortsHlsWarmup(
	initialProperty.value.featuredVideos?.[0]?.url,
	commercialId
);

// The Shaka chunk (~764 KB) is a dynamic import that normally only starts
// downloading when the player mounts — serially after hydration. Kick it off
// now so it downloads in parallel with hydration and the feed API. Skipped on
// iOS native playback, which never loads Shaka.
if (import.meta.client) {
	setIOSMsePreference(useFeatureFlags().ft_shorts_ios_mse);
	if (!isIOSNativePlayback()) {
		void loadShaka().catch(() => {});
	}
}

const {
	reelItems,
	historyCount,
	hasMore,
	hasMoreHistory,
	isLoading,
	loadHistory,
	loadMoreHistory,
	loadMoreFeed,
	setTargetFromProperty,
	resetAll,
	resetHistoryAndFeed,
	seedFromCarousel
} = useShortsFeedOrchestrator();

const entryContext = useShortsEntryContext();
const wasOpenedFromShortsClick = consumeShortsAutoplayBlessing();
const currentIndex = ref(0);
const isAutoplayArmed = ref(
	entryContext.value !== null || wasOpenedFromShortsClick
);
const currentItem = ref<ReelItem | null>(null);
const currentCommercialId = computed(
	() => currentItem.value?.property?.commercialId ?? commercialId
);
const { isEntryPropertyAvailable, isUnavailableEntryState } =
	useShortsEntryAvailability(initialProperty, {
		shouldAddRobotsMeta: computed(
			() => currentCommercialId.value === commercialId
		)
	});

useShortsVideoJsonLd(() => initialProperty.value);

if (isEntryPropertyAvailable.value && !isClusterFeedEntry) {
	setTargetFromProperty(initialProperty.value);
	currentItem.value = reelItems.value[currentIndex.value] ?? null;
}

const isPageReady = ref(true);
const isClusterFeedResolved = ref(false);
const isOpen = ref(true);
const isMobile = ref(false);
const isGlobalMuted = ref(false);
const isContactModalOpen = ref(false);

const { isPausedBecauseContactModal, notifyContactModalOpenChange } =
	useShortsContactModalVideoPause();

const { isHolding: isSpeedHolding } = useShortsSpeedState();

watch(isContactModalOpen, notifyContactModalOpenChange);

const {
	isSearchDialogOpen,
	shortsSearchGeoOnly,
	selectionText,
	currentFiltersObject,
	cities,
	regions,
	macroregions,
	states,
	locationLabel,
	shortsSearchSuggestBus,
	handleSearchTermUpdate,
	handleShortsSearchClearLocation,
	handleEmptyResultOnFilter
} = useShortsReelLocationSearch();

const pilarLogoSvg = (LinearPilarLogoRaw as unknown as string).replace(
	/<svg/,
	'<svg width="100%" height="100%"'
);

const currentProperty = computed(() => currentItem.value?.property);
const seoProperty = computed(
	() => currentProperty.value ?? initialProperty.value ?? undefined
);
const currentPropertyBrokerForContact = computed<Agent | undefined>(() => {
	const agent = currentProperty.value?.agent;
	if (!agent) return undefined;

	if ('id' in agent && 'profilePicture' in agent) {
		return agent;
	}

	return {
		id: '',
		name: agent.name,
		profilePicture: {
			url: agent.profilePictureUrl,
			croppedUrl: agent.profilePictureUrl
		}
	};
});
const currentPropertyCompanyLogoForContact = computed(() => {
	const company = currentProperty.value?.company;
	if (!company) return undefined;
	return 'smallLogo' in company ? company.smallLogo : company.logoUrl;
});

const desktopItemInfo = useShortsPropertyCardTexts(currentProperty);

const currentPropertyId = computed(() => currentProperty.value?.id ?? '');

const {
	trackFormPropertyClicked,
	trackSearchBarClicked,
	trackReturn,
	trackCtaViewMoreClicked,
	trackPageView
} = useEvents();

const shortsMixpanelEntry = computed(() => ({
	entryCommercialId: commercialId,
	entryAskingPrice: initialProperty.value?.askingPrice ?? null,
	entryRegionName: initialProperty.value?.region ?? ''
}));

const {
	isVisible: isOnboardingVisible,
	show: showOnboarding,
	dismiss: dismissOnboarding
} = useShortsFeedOnboarding({
	enabled: true,
	onDismiss: reason => {
		if (reason !== 'cta') return;
		trackCtaViewMoreClicked(
			buildShortsOnboardingCtaPayload({
				property: currentProperty.value ?? null,
				currentPage: route.fullPath,
				entry: shortsMixpanelEntry.value
			})
		);
	}
});

const desktopFavoriteEventData = computed(() => {
	const p = currentProperty.value;
	if (!p) return undefined;
	return buildShortsFavoriteEventData({
		property: p,
		currentPage: route.fullPath,
		companyName: desktopItemInfo.companyName.value,
		agentName: desktopItemInfo.agentName.value,
		entry: shortsMixpanelEntry.value
	});
});

const {
	isFavorite: desktopIsFavorite,
	isValidData: desktopIsFavoriteDataValid,
	handleToggleFavorite: desktopOriginalToggleFavorite
} = useToggleFavoriteSync(
	currentPropertyId,
	currentCommercialId,
	desktopFavoriteEventData,
	'shorts',
	undefined,
	{
		onLoginModalOverlayOpenChange: notifyContactModalOpenChange,
		deferLoginUntilCount: SHORTS_DEFER_LOGIN_FAVORITE_COUNT
	}
);

const FAVORITE_MAIN_PULSE_DURATION_MS = 250;
const FAVORITE_BACKGROUND_PULSE_GAP_MS = 250;
const FAVORITE_BACKGROUND_PULSE_DURATION_MS = 350;

const desktopFavoritePulse = useFavoriteStarPulse({
	isEnabled: computed(() => true),
	isSelected: desktopIsFavorite,
	// Match the previous behavior used in this page
	firstPulseDelayMs: 0,
	firstPulseDurationMs: FAVORITE_MAIN_PULSE_DURATION_MS,
	celebrationFirstDelayMs: 0,
	celebrationGapBetweenPulsesMs: FAVORITE_BACKGROUND_PULSE_GAP_MS,
	secondPulseDurationMs: FAVORITE_BACKGROUND_PULSE_DURATION_MS
});
const isDesktopFavoriteMainPulsing = computed(
	() => desktopFavoritePulse.isMainPulsing.value
);
const isDesktopFavoriteBackgroundPulsing = computed(
	() => desktopFavoritePulse.isBackgroundPulsing.value
);

async function handleDesktopToggleFavorite() {
	await desktopOriginalToggleFavorite();
}

const desktopSharePromoText = 'Confira este imóvel com o PilarHomes Shorts:';

const {
	filters,
	hasActiveFilters,
	isFilterDropdownOpen,
	toQueryParams,
	fromQueryParams,
	applyFiltersAsync
} = useExploreFilters({
	getBaseFilters: ({ hasActiveFilters }) => {
		if (hasShortsListingQueryContext(route.query)) {
			return withFeedBaseFilters(shortsListingQueryToFilters(route.query));
		}

		return withImagesEvery({
			...applyShortsEntryRecommendationRule(
				{},
				hasActiveFilters,
				initialProperty.value
			),
			...(historyCount.value > 0 ? { onlyUnwatched: true } : {})
		});
	},
	getFetchOptions: () => ({
		enableRecommendationFallback: false
	})
});

const { syncRouteToCurrentItem } = useShortsRouteSync({
	currentCommercialId,
	toQueryParams: () => ({
		...toQueryParams(),
		...(isClusterFeedEntry ? { entrySource: SHORTS_ENTRY_SOURCE.CLUSTER } : {})
	})
});

useShortsFeedSeo(seoProperty);

const isVideoPaused = computed(
	() =>
		isFilterDropdownOpen.value ||
		isPausedBecauseContactModal.value ||
		isOnboardingVisible.value
);

const isReady = computed(
	() =>
		isPageReady.value &&
		(reelItems.value.length > 0 ||
			isUnavailableEntryState.value ||
			(isClusterFeedEntry && isClusterFeedResolved.value))
);

const bgCanvasRef = ref<HTMLCanvasElement | null>(null);

const {
	startAnimation: startBgAnimation,
	resetVideoCache: resetBgVideoCache,
	isLowPerformance: isBgLowPerformance
} = useShortsBackgroundBlur({
	canvasRef: bgCanvasRef
});

watch(currentIndex, () => {
	resetBgVideoCache();
});

const didTrackDesktopShortsSearchBar = ref(false);

function trackShortsSearchBarClicked() {
	if (!import.meta.client) return;
	trackSearchBarClicked({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.COMMERCIAL_ID]: currentCommercialId.value || commercialId
	});
}

function handleMobileShortsSearchOpen() {
	trackShortsSearchBarClicked();
	isSearchDialogOpen.value = true;
}

function handleDesktopShortsSearchInteract() {
	if (didTrackDesktopShortsSearchBar.value) return;
	didTrackDesktopShortsSearchBar.value = true;
	trackShortsSearchBarClicked();
}

watch(isContactModalOpen, isOpen => {
	if (!import.meta.client || !isOpen) return;
	const p = currentProperty.value;
	if (!p) return;
	trackFormPropertyClicked(
		buildShortsFormPropertyClickedPayload({
			property: p,
			currentPage: route.fullPath,
			isLoggedIn: userSession.loggedIn.value,
			entry: shortsMixpanelEntry.value
		})
	);
});

const emptyStateTitle = computed(() =>
	hasActiveFilters.value ? 'Nenhum short encontrado' : 'Nenhum short disponível'
);

const emptyStateDescription = computed(() =>
	hasActiveFilters.value
		? 'Ajuste os filtros para ver mais resultados'
		: 'Não há shorts disponíveis no momento'
);

const hasAppliedFilters = computed(() => hasShortsAppliedFilters(route.query));

function handleCurrentItemChange(item: ReelItem | null): void {
	currentItem.value = item;
}

function handleIndexChange(newIndex: number, reelIndex: number): void {
	currentIndex.value = newIndex;

	if (isPageReady.value) {
		syncRouteToCurrentItem();
	}

	if (reelIndex < 0) return;

	// Preload more feed when close to the end
	const itemsFromEnd = reelItems.value.length - reelIndex - 1;
	if (hasMore.value && !isLoading.value && itemsFromEnd <= 3) {
		void loadMoreFeed();
	}

	// Preload more history when close to the beginning
	if (hasMoreHistory.value && !isLoading.value && reelIndex <= 3) {
		void loadMoreHistory();
	}
}

function navigateToValidIndex(targetIndex: number): void {
	if (targetIndex >= 0 && targetIndex < reelItems.value.length) {
		currentIndex.value = targetIndex;
	}
}

function navigateToLastHistoryItem(): void {
	if (historyCount.value > 0) {
		navigateToValidIndex(historyCount.value - 1);
	}
}

async function handleRequestHistory() {
	// If we have loaded history items, navigate to the last one first
	if (historyCount.value > 0 && currentIndex.value >= historyCount.value) {
		navigateToLastHistoryItem();
		return;
	}

	// At the start of history — load more if available
	if (!hasMoreHistory.value) return;

	const previousHistoryCount = historyCount.value;
	const addedItems = await loadMoreHistory();
	await nextTick();

	if (reelItems.value.length === 0) return;

	if (addedItems > 0) {
		const newItemsCount = historyCount.value - previousHistoryCount;
		navigateToValidIndex(Math.max(0, newItemsCount - 1));
	}
}

async function navigateAwayFromShorts() {
	if (
		import.meta.client &&
		typeof window !== 'undefined' &&
		window.history.length > 1
	) {
		try {
			await router.back();
			return;
		} catch {
			// Aborted navigation — fall through to home
		}
	}

	await router.replace('/');
}

function handleHeaderBack() {
	if (import.meta.client) {
		trackReturn({
			[MixpanelProps.CURRENT_PAGE]: route.fullPath,
			[MixpanelProps.DESTINATION_URL]:
				(window.history.state?.back as string) ?? '/'
		});
	}
	void navigateAwayFromShorts();
}

function handleReelsFeedClose() {
	void navigateAwayFromShorts();
}

function handleDesktopPropertyClick() {
	const prop = currentProperty.value;
	if (!prop) return;
	const url = getPropertyDetailsPath(prop.commercialId, prop.slug);
	if (import.meta.client) {
		trackCtaViewMoreClicked(
			buildShortsCtaViewMorePayload({
				property: prop,
				currentPage: route.fullPath,
				destinationUrl: url,
				entry: shortsMixpanelEntry.value
			})
		);
	}
	navigateToPropertyDetails(prop.commercialId, prop.slug);
}

function handleDesktopContactClick() {
	// Defer open so the same pointer event is not treated as interact-outside by the dialog
	void nextTick(() => {
		isContactModalOpen.value = true;
	});
}

watch(
	() => filters,
	() => {
		if (!isPageReady.value || !import.meta.client) return;
		syncRouteToCurrentItem();
	},
	{ deep: true }
);

const breakpoints = useBreakpoints(breakpointsTailwind);

watch(breakpoints.smaller('lg'), value => {
	isMobile.value = value;
});
onMounted(async () => {
	isMobile.value = breakpoints.smaller('lg').value;

	trackPageView({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.PREVIOUS_PAGE_URL]: document.referrer,
		[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(initialProperty.value)
	});
	mixpanel.trackPageview({
		[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(initialProperty.value)
	});

	fromQueryParams(route.query, false);

	// Reset transient feed state before choosing the entry strategy.
	resetHistoryAndFeed();
	currentIndex.value = 0;

	if (isClusterFeedEntry) {
		try {
			await applyFiltersAsync();
		} catch {
			// The feed handles an empty result after the request resolves.
		} finally {
			isClusterFeedResolved.value = true;
		}

		currentIndex.value = 0;
		await nextTick();
		syncRouteToCurrentItem();
		isAutoplayArmed.value = true;
		startBgAnimation();
		showOnboarding();
		return;
	}

	if (isUnavailableEntryState.value) {
		isAutoplayArmed.value = true;
		void applyFiltersAsync();
		startBgAnimation();
		return;
	}

	// Load history client-side (uses localStorage, not available during SSR)
	await loadHistory();

	// Recalculate index since history prepends items before the target.
	// Skip history items to find the actual target position (history may contain the same video).
	const targetIndex = reelItems.value.findIndex(
		item => item.property.commercialId === commercialId && !item.isFromHistory
	);
	if (targetIndex >= 0) currentIndex.value = targetIndex;

	await nextTick();
	syncRouteToCurrentItem();

	isAutoplayArmed.value = true;

	if (entryContext.value) {
		const { items, filters, page, perPage, hasMore } = entryContext.value;
		entryContext.value = null;
		const feedSeedOptions: Parameters<typeof seedFromCarousel>[2] = {
			currentPage: page,
			hasMore
		};
		if (perPage !== undefined) {
			feedSeedOptions.perPage = perPage;
		}
		seedFromCarousel(items, withFeedBaseFilters(filters), feedSeedOptions);
	} else {
		void applyFiltersAsync();
	}

	startBgAnimation();

	showOnboarding();
});
</script>

<template>
	<div class="fixed inset-0 z-50 bg-black/80 max-md:backdrop-blur-md">
		<!-- eslint-disable vue/no-v-html -->
		<Teleport to="body">
			<header
				:class="[
					'pointer-events-none fixed inset-x-0 top-0 z-200 flex h-[80px] lg:h-20 lg:px-12 items-center gap-4 bg-linear-to-b to-transparent px-4 transition-opacity duration-200',
					isSpeedHolding ? 'opacity-0 invisible' : 'opacity-100'
				]"
			>
				<button
					type="button"
					class="pointer-events-auto shrink-0 text-white cursor-pointer"
					aria-label="Voltar"
					@click="handleHeaderBack"
				>
					<IconMaterialSymbolsLightArrowBack class="size-6" />
				</button>

				<NuxtLink
					to="/"
					aria-label="Página inicial"
					class="pointer-events-auto shrink-0"
				>
					<div class="h-4 w-[132px]" v-html="pilarLogoSvg" />
				</NuxtLink>

				<div class="pointer-events-auto ml-auto flex items-center gap-2">
					<GlassButton
						variant="action"
						size="sm"
						class="xl:hidden"
						aria-label="Buscar lugares"
						data-testid="shorts-reels-feed-search-trigger"
						@click="handleMobileShortsSearchOpen"
					>
						<IconMaterialSymbolsLightSearch class="size-5 text-white" />
					</GlassButton>
					<GlassButton
						variant="action"
						size="sm"
						class="md:hidden"
						:aria-label="isGlobalMuted ? 'Ativar som' : 'Desativar som'"
						@click="isGlobalMuted = !isGlobalMuted"
					>
						<IconMaterialSymbolsLightVolumeOff
							v-if="isGlobalMuted"
							class="size-5 text-white"
						/>
						<IconMaterialSymbolsLightVolumeUp
							v-else
							class="size-5 text-white"
						/>
					</GlassButton>
				</div>
			</header>
		</Teleport>

		<Transition
			leave-active-class="transition-opacity duration-300 ease-out"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div v-if="!isReady" class="fixed inset-0 z-150 bg-black">
				<img
					v-if="initialProperty?.featuredVideos?.[0]?.thumbnail"
					:src="initialProperty.featuredVideos[0].thumbnail"
					:alt="initialProperty.propertySummary"
					class="h-full w-full object-cover"
				/>
				<div class="absolute inset-x-0 bottom-0 z-10 p-1">
					<div class="h-[2px] w-full overflow-hidden">
						<div class="h-full w-full animate-video-loading-bar bg-white" />
					</div>
				</div>
			</div>
		</Transition>

		<div class="sr-only lg:hidden" aria-hidden="true">
			<ShortsSearchSuggestDialog
				ref="shortsMobileSearchDialog"
				v-model:is-open="isSearchDialogOpen"
				:show-input-button="false"
				v-bind="shortsSearchGeoOnly"
				:lock-body-scroll="false"
				dialog-overlay-extra-class="z-[260]"
				dialog-content-extra-class="z-[261]"
				variant="dark"
				:search-term="selectionText"
				:current-filters="currentFiltersObject"
				:cities="cities"
				:regions="regions"
				:macroregions="macroregions"
				:states="states"
				class="border-none shadow-none"
				list-wrapper-classes="z-[110] mt-[11px] md:mt-3 lg:mt-4"
				:placeholders="locationLabel || 'Busque lugares'"
				focused-placeholder="Busque lugares"
				:bus="shortsSearchSuggestBus"
				@update:search-term="handleSearchTermUpdate"
				@clear="handleShortsSearchClearLocation"
			/>
		</div>

		<PropertyReelsFeed
			v-model="isOpen"
			:shorts-entry="shortsMixpanelEntry"
			:items="reelItems"
			:initial-index="currentIndex"
			:is-loading="isLoading"
			:is-ready="isReady"
			:autoplay-armed="isAutoplayArmed"
			:has-more="hasMore"
			:has-more-history="hasMoreHistory"
			:history-count="historyCount"
			:has-leading-item="isUnavailableEntryState"
			:show-explore-button="false"
			:is-paused="isVideoPaused"
			:disable-scroll="isOnboardingVisible"
			:empty-state-title="emptyStateTitle"
			:empty-state-description="emptyStateDescription"
			:initial-muted="isGlobalMuted"
			:disable-keyboard="isSearchDialogOpen || isOnboardingVisible"
			show-navigation
			@close="handleReelsFeedClose"
			@current-item-change="handleCurrentItemChange"
			@index-change="handleIndexChange"
			@load-more="loadMoreFeed"
			@request-history="handleRequestHistory"
		>
			<template #background>
				<div class="h-full w-full scale-125 blur-2xl transform-gpu">
					<NuxtImg
						v-if="
							isBgLowPerformance &&
							(currentItem?.thumbnailUrl ||
								initialProperty?.featuredVideos?.[0]?.thumbnail)
						"
						:src="
							currentItem?.thumbnailUrl ||
							initialProperty?.featuredVideos?.[0]?.thumbnail
						"
						format="webp"
						:quality="50"
						width="160"
						densities="x1"
						loading="lazy"
						class="h-full w-full object-cover"
						aria-hidden="true"
					/>
					<canvas
						v-else
						ref="bgCanvasRef"
						aria-hidden="true"
						class="h-full w-full object-cover"
					/>
				</div>
				<div class="absolute inset-0 bg-black/60" />
			</template>
			<template #leading-item>
				<ShortsUnavailableCard />
			</template>

			<template #above-feed>
				<div
					class="relative w-full shrink-0 md:pt-20 xl:pt-0"
					data-testid="shorts-reels-feed-search-anchor"
					@pointerdown.capture="handleDesktopShortsSearchInteract"
				>
					<ShortsSearchSuggestInline
						field-class="hidden xl:block w-full border-none"
						:search-term="selectionText"
						:location-label="locationLabel"
						:cities="cities"
						:regions="regions"
						:macroregions="macroregions"
						:states="states"
						:current-filters="currentFiltersObject"
						:bus="shortsSearchSuggestBus"
						@update:search-term="handleSearchTermUpdate"
						@empty-result-on-filter="handleEmptyResultOnFilter"
						@clear="handleShortsSearchClearLocation"
					>
						<template #input-prefix>
							<div class="flex h-full items-center pl-3">
								<IconMaterialSymbolsLightSearch class="size-5 text-beige" />
							</div>
						</template>
					</ShortsSearchSuggestInline>
				</div>
			</template>
			<template #left-sidebar="{ currentItem: slotItem }">
				<ReelPropertyInfo
					v-if="slotItem?.property"
					class="hidden xl:flex relative inset-auto z-auto w-full py-0"
					:property="slotItem.property"
					:formatted-price="desktopItemInfo.formattedPrice.value"
					:formatted-area="desktopItemInfo.formattedArea.value"
					:rooms-info="desktopItemInfo.roomsInfo.value"
					:region="desktopItemInfo.region.value"
					:city-state="desktopItemInfo.cityState.value"
					:agent-name="desktopItemInfo.agentName.value"
					:agent-first-name="desktopItemInfo.agentFirstName.value"
					:company-name="desktopItemInfo.companyName.value"
					:broker-src="desktopItemInfo.brokerSrc.value"
					:company-src="desktopItemInfo.companySrc.value"
					:show-progress-bar="false"
					:show-action-buttons="false"
					@property-click="handleDesktopPropertyClick"
					@contact-click="handleDesktopContactClick"
				/>
			</template>
			<template #right-sidebar="{ currentItem: slotItem }">
				<ReelActionButtons
					v-if="slotItem?.property"
					class="hidden lg:flex lg:mt-auto"
					:property="slotItem.property"
					:is-favorite="desktopIsFavorite"
					:is-favorite-data-valid="desktopIsFavoriteDataValid"
					:is-favorite-main-pulsing="isDesktopFavoriteMainPulsing"
					:is-favorite-background-pulsing="isDesktopFavoriteBackgroundPulsing"
					:is-share-animating="false"
					:share-promo-text="desktopSharePromoText"
					@toggle-favorite="handleDesktopToggleFavorite"
				/>
			</template>
		</PropertyReelsFeed>

		<ShortsOnboarding v-if="isOnboardingVisible" @dismiss="dismissOnboarding" />

		<ClientOnly>
			<LazyContactFormModal
				v-model:model-value="isContactModalOpen"
				:property-id="currentProperty?.id"
				:property-commercial-id="currentProperty?.commercialId"
				:asking-price="currentProperty?.askingPrice ?? null"
				:broker="currentPropertyBrokerForContact"
				:company-name="currentProperty?.company?.name"
				:company-logo="currentPropertyCompanyLogoForContact"
				:analytics-current-page="route.fullPath"
				:video-shorts="(currentProperty?.featuredVideos?.length ?? 0) > 0"
				context="property"
				:default-expanded="false"
			/>
		</ClientOnly>
	</div>
</template>
